import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ActionsData,
  MenuButton,
  MenuItem,
  MenuItemGroup,
  RyeconEllipsisHorizontal,
  Sizes,
  toast,
} from '@dbt-labs/sourdough';

import { Spinner } from '@/components/Spinner';
import { GenericResourceNode, useGenericResource } from '@/hooks/discovery/types';
import { useOpenIn } from '@/hooks/useOpenIn';
import { NavigationResourceType } from '@/panel/ResourcesFilter/selectedResource';
import { parseUniqueId } from '@/typings/parseUniqueId';

// Extract the singular action type from ActionsData['actions'] array
type ActionItem = NonNullable<ActionsData['actions']>[number];

const LOADING_ACTION_ITEMS: MenuItem[] = [
  {
    label: 'Loading...',
    ryecon: Spinner,
  },
];

const ActionRenderer = React.memo(
  ({
    projectId,
    assetId,
    environmentId,
    assetType,
    initialActions,
  }: {
    projectId: number;
    assetId: string;
    environmentId: number;
    assetType: string;
    initialActions?: ActionItem;
  }) => {
    const parsedAssetId = parseUniqueId(assetId);
    const [shouldFetch, setShouldFetch] = useState(false);
    const { data, fetching, error } = useGenericResource(parsedAssetId, {
      environmentId,
      pause: !shouldFetch,
    });

    useEffect(() => {
      if (error) {
        toast.error(error.message);
      }
    }, [error]);

    return (
      <ActionRendererContent
        isLoadingAsset={fetching || !shouldFetch}
        data={data ?? {}}
        assetType={assetType as NavigationResourceType}
        projectId={projectId}
        environmentId={environmentId}
        initialActions={initialActions}
        onMenuToggleClick={() => setShouldFetch(true)}
      />
    );
  },
);

const ActionRendererContent = React.memo(
  ({
    data,
    assetType,
    initialActions,
    projectId,
    environmentId,
    isLoadingAsset,
    onMenuToggleClick,
  }: {
    data: GenericResourceNode;
    assetType: NavigationResourceType;
    projectId: number;
    environmentId: number;
    isLoadingAsset: boolean;
    initialActions?: ActionItem;
    onMenuToggleClick?: () => void;
  }) => {
    const {
      actionResolvers,
      isFetching: isFetchingOpenIn,
      openInItems,
    } = useOpenIn({
      files: [data?.patchPath, data?.filePath],
      packageName: data?.packageName,
      resourceType: assetType as NavigationResourceType,
      exposureUrl: data?.url,
      uniqueId: data?.uniqueId,
      projectId,
      environmentId,
    });

    const isFetching = isFetchingOpenIn || isLoadingAsset;

    const hydratedActions: (MenuItemGroup | MenuItem)[] = useMemo(() => {
      const parsedOpenInItems = openInItems
        .map((item) => {
          if ('items' in item) {
            return {
              label: item.label,
              ryecon: item.ryecon,
              items: item.items,
              includeTopDivider: item.includeTopDivider,
              show: item.show,
            };
          }
          return {
            label: item.label,
            ryecon: item.ryecon,
            value: item.value,
            includeTopDivider: item.includeTopDivider,
            show: item.show,
          };
        })
        .filter((item) => item.show);
      const parsedInitialActions = [...(initialActions?.items ?? [])];
      if (parsedOpenInItems.length > 0 && parsedInitialActions.length > 0) {
        parsedInitialActions[0] = {
          ...parsedInitialActions[0],
          includeTopDivider: true,
        };
      }
      return [...parsedOpenInItems, ...parsedInitialActions];
    }, [initialActions?.items, openInItems]);

    const handleClick = useCallback(
      (value: string | number) => {
        if (value === 'favorite' || value === 'share') {
          initialActions?.onMenuItemClick(value);
        } else {
          actionResolvers[value as keyof typeof actionResolvers]();
        }
      },
      [initialActions, actionResolvers],
    );

    return (
      <>
        <MenuButton
          testId="action-renderer-menu-button"
          items={isFetching ? LOADING_ACTION_ITEMS : hydratedActions}
          onMenuToggleClick={onMenuToggleClick}
          onClick={handleClick}
          ryecon={RyeconEllipsisHorizontal}
          buttonStyle="tertiary"
          size={Sizes.sm}
          {...(initialActions?.menuButtonProps ?? {})}
        />
      </>
    );
  },
);

export default ActionRenderer;
